import { act, renderHook, waitFor } from '@testing-library/react'
import { beforeEach, describe, expect, it, vi } from 'vitest'

const postMock = vi.fn()
const runtimeVarsMock = vi.fn()
const ticketAccessMock = vi.fn()

vi.mock('app-kit', () => ({
  useRuntimeVariables: () => runtimeVarsMock(),
}))

vi.mock('features/auth', () => ({
  useGeckoAuth: () => ({ authorizedAxios: { post: postMock } }),
}))

vi.mock('features/libra/ticket/TicketAccessContext', () => ({
  useTicketAccess: () => ticketAccessMock(),
}))

vi.mock('features/libra/document/useDocumentProcessingReady', () => ({
  useDocumentProcessingReady: () => ({
    allProcessed: true,
    isCheckingProcessing: false,
    unprocessedCount: 0,
  }),
}))

vi.mock('features/permissions', () => ({
  PermissionsGate: ({ children }: { children: React.ReactNode }) => children,
}))

vi.mock('features/libra/document/DocumentDataContext', () => ({
  DocumentDataContext: {},
}))

vi.mock('features/libra/document/LibraDocumentContext', () => ({
  LibraDocumentContext: {},
}))

import { useGongAgentAutofill } from './GongAgentAutofill'

describe('useGongAgentAutofill', () => {
  beforeEach(() => {
    postMock.mockReset()
    runtimeVarsMock.mockReset()
    ticketAccessMock.mockReset()
    runtimeVarsMock.mockReturnValue({ GONG_AGENT_URL: 'http://fake' })
    ticketAccessMock.mockReturnValue({ ticketId: 't1' })
  })

  it('posts to /api/v1/assess/section with ticket_id and section_id', async () => {
    postMock.mockResolvedValue({
      data: { field_values: {}, documents_used: [] },
    })
    const onFieldValues = vi.fn()
    const { result } = renderHook(() =>
      useGongAgentAutofill({ sectionId: 's1', onFieldValues })
    )
    await act(async () => {
      await result.current.autofill()
    })
    expect(postMock).toHaveBeenCalledTimes(1)
    expect(postMock).toHaveBeenCalledWith('http://fake/api/v1/assess/section', {
      ticket_id: 't1',
      section_id: 's1',
    })
  })

  it('forwards inputs verbatim in the POST body when provided (zone suggestion)', async () => {
    postMock.mockResolvedValue({
      data: { field_values: {}, documents_used: [] },
    })
    const onFieldValues = vi.fn()
    const { result } = renderHook(() =>
      useGongAgentAutofill({
        sectionId: 'zone_analysis_section',
        onFieldValues,
        inputs: { zone_reference_image_id: 'img-1' },
      })
    )
    await act(async () => {
      await result.current.autofill()
    })
    expect(postMock).toHaveBeenCalledWith('http://fake/api/v1/assess/section', {
      ticket_id: 't1',
      section_id: 'zone_analysis_section',
      inputs: { zone_reference_image_id: 'img-1' },
    })
  })

  it('passes the server field_values straight through to onFieldValues, with llmValue riding along', async () => {
    const serverFieldValues = {
      inspectorName: {
        value: 'Alice',
        source: null,
        llmValue: 'Alice',
      },
      severity: {
        value: 'high',
        source: { type: 'document', value: 'doc-1' },
        llmValue: 'high',
      },
      legacyFieldNoLlmValue: {
        value: 42,
        source: null,
      },
    }
    postMock.mockResolvedValue({
      data: { field_values: serverFieldValues, documents_used: ['doc-1'] },
    })
    const onFieldValues = vi.fn()
    const { result } = renderHook(() =>
      useGongAgentAutofill({ sectionId: 's1', onFieldValues })
    )
    await act(async () => {
      await result.current.autofill()
    })
    expect(onFieldValues).toHaveBeenCalledTimes(1)
    const arg = onFieldValues.mock.calls[0][0]
    expect(arg).toEqual(serverFieldValues)
    expect(arg.inspectorName.llmValue).toBe('Alice')
    expect(arg.severity.llmValue).toBe('high')
    expect('llmValue' in arg.legacyFieldNoLlmValue).toBe(false)
  })

  it('only reads ticketId from the ticket access context (no setLlmFieldValues, no setFieldValues)', async () => {
    // Spy: assert that the mocked useTicketAccess return value is read only
    // for ticketId. If the hook ever started calling a setter from the
    // context, this test would need to be updated to mock that setter —
    // which is exactly the regression guard we want.
    const ticketAccessReturnValue = { ticketId: 't1' }
    ticketAccessMock.mockReturnValue(ticketAccessReturnValue)

    postMock.mockResolvedValue({
      data: { field_values: {}, documents_used: [] },
    })
    const onFieldValues = vi.fn()
    const { result } = renderHook(() =>
      useGongAgentAutofill({ sectionId: 's1', onFieldValues })
    )
    await act(async () => {
      await result.current.autofill()
    })
    // The context return value should remain exactly what we gave it —
    // the hook must not mutate it or attach anything.
    expect(ticketAccessReturnValue).toEqual({ ticketId: 't1' })
  })

  it('sets error from err.response.data.detail and does not call onFieldValues on failure', async () => {
    postMock.mockRejectedValue({
      response: { data: { detail: 'boom' } },
    })
    const onFieldValues = vi.fn()
    const { result } = renderHook(() =>
      useGongAgentAutofill({ sectionId: 's1', onFieldValues })
    )
    await act(async () => {
      await result.current.autofill()
    })
    await waitFor(() => {
      expect(result.current.error).toBe('boom')
    })
    expect(onFieldValues).not.toHaveBeenCalled()
  })

  it('falls back to a generic error message when detail is absent', async () => {
    postMock.mockRejectedValue(new Error('network down'))
    const onFieldValues = vi.fn()
    const { result } = renderHook(() =>
      useGongAgentAutofill({ sectionId: 's1', onFieldValues })
    )
    await act(async () => {
      await result.current.autofill()
    })
    await waitFor(() => {
      expect(result.current.error).toBe('AI autofill failed')
    })
    expect(onFieldValues).not.toHaveBeenCalled()
  })

  it('does not POST when GONG_AGENT_URL is missing and sets a config error', async () => {
    runtimeVarsMock.mockReturnValue({ GONG_AGENT_URL: '' })
    const onFieldValues = vi.fn()
    const { result } = renderHook(() =>
      useGongAgentAutofill({ sectionId: 's1', onFieldValues })
    )
    await act(async () => {
      await result.current.autofill()
    })
    expect(postMock).not.toHaveBeenCalled()
    expect(result.current.error).toBe('GONG_AGENT_URL is not configured')
    expect(onFieldValues).not.toHaveBeenCalled()
  })

  it('captures documents_used from the response', async () => {
    postMock.mockResolvedValue({
      data: { field_values: {}, documents_used: ['doc-a', 'doc-b'] },
    })
    const onFieldValues = vi.fn()
    const { result } = renderHook(() =>
      useGongAgentAutofill({ sectionId: 's1', onFieldValues })
    )
    await act(async () => {
      await result.current.autofill()
    })
    await waitFor(() => {
      expect(result.current.documentsUsed).toEqual(['doc-a', 'doc-b'])
    })
  })

  it('stamps stable _id on array-row values missing one before handing them to onFieldValues', async () => {
    const serverFieldValues = {
      defects: {
        value: [{ note: 'crack A' }, { note: 'crack B' }],
        source: null,
        llmValue: [{ note: 'crack A' }, { note: 'crack B' }],
      },
    }
    postMock.mockResolvedValue({
      data: { field_values: serverFieldValues, documents_used: [] },
    })
    const onFieldValues = vi.fn()
    const { result } = renderHook(() =>
      useGongAgentAutofill({ sectionId: 's1', onFieldValues })
    )
    await act(async () => {
      await result.current.autofill()
    })
    expect(onFieldValues).toHaveBeenCalledTimes(1)
    const arg = onFieldValues.mock.calls[0][0]
    const rows = arg.defects.value
    expect(rows).toHaveLength(2)
    expect(typeof rows[0]._id).toBe('string')
    expect(rows[0]._id.length).toBeGreaterThan(0)
    expect(typeof rows[1]._id).toBe('string')
    expect(rows[0]._id).not.toBe(rows[1]._id)
    expect(rows[0].note).toBe('crack A')
    expect(rows[1].note).toBe('crack B')
  })

  it('preserves wrapper content when every array-row already has an _id', async () => {
    const defectsWrapper = {
      value: [
        { _id: 'row-1', note: 'crack A' },
        { _id: 'row-2', note: 'crack B' },
      ],
      source: null,
      llmValue: [
        { _id: 'row-1', note: 'crack A' },
        { _id: 'row-2', note: 'crack B' },
      ],
    }
    const serverFieldValues = { defects: defectsWrapper }
    postMock.mockResolvedValue({
      data: { field_values: serverFieldValues, documents_used: [] },
    })
    const onFieldValues = vi.fn()
    const { result } = renderHook(() =>
      useGongAgentAutofill({ sectionId: 's1', onFieldValues })
    )
    await act(async () => {
      await result.current.autofill()
    })
    const arg = onFieldValues.mock.calls[0][0]
    // Reference-equality to the raw axios payload is not preserved because
    // the ingestion path zod-parses the payload into a validated copy. What
    // matters here is that stampArrayRowIds is a no-op when every row is
    // already stamped, so content round-trips unchanged.
    expect(arg.defects).toEqual(defectsWrapper)
  })

  it('does not stamp _id onto rows inside llmValue (lineage is immutable, stringified only)', async () => {
    const serverFieldValues = {
      defects: {
        value: [{ note: 'crack A' }],
        source: null,
        llmValue: [{ note: 'crack A' }],
      },
    }
    postMock.mockResolvedValue({
      data: { field_values: serverFieldValues, documents_used: [] },
    })
    const onFieldValues = vi.fn()
    const { result } = renderHook(() =>
      useGongAgentAutofill({ sectionId: 's1', onFieldValues })
    )
    await act(async () => {
      await result.current.autofill()
    })
    const arg = onFieldValues.mock.calls[0][0]
    expect(arg.defects.value[0]._id).toBeDefined()
    expect(arg.defects.llmValue[0]._id).toBeUndefined()
  })
})
